import { HttpClient, HttpContext } from '@angular/common/http';
import { DestroyRef, inject, Injectable, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Observable, catchError, of, switchMap, timer } from 'rxjs';
import { filter, map, takeWhile } from 'rxjs/operators';
import { BYPASS_TOKEN_INJECTION } from '../../core/interceptors/fsx.interceptor.service';

/** Public entry document polled for a changed asset set (a new deploy). */
const INDEX_URL = '/index.html';

/** Default poll cadence for the deployed-version check. */
const POLL_INTERVAL_MS = 5 * 60 * 1000;

/**
 * Extracts hashed `.js` / `.css` asset URLs from index.html to form a build
 * signature. Excludes absolute (external CDN) URLs so the signal tracks only the
 * app's own build assets, not third-party markup.
 */
const ASSET_PATTERN = /(?:src|href)="(?!https?:)([^"]+\.(?:js|css))"/g;

/**
 * Detects when a newer build has been deployed while the current tab is still open,
 * so the app can prompt a refresh before the stale tab hits a missing-chunk error
 * (the preventive counterpart to {@link ChunkRecoveryService}). Polls index.html and
 * compares its hashed asset set against the one captured at startup; content-hashed
 * filenames change on every deploy, so any difference means a new build.
 */
@Injectable({ providedIn: 'root' })
export class VersionCheckService {
  /** Angular HttpClient used to fetch index.html. */
  private readonly http = inject(HttpClient);

  /** Ties polling teardown to the root injector's lifetime. */
  private readonly destroyRef = inject(DestroyRef);

  /** Asset signature captured at startup; the baseline every poll is compared against. */
  private baselineSignature: string | null = null;

  /** Guards against starting the poll loop more than once. */
  private started = false;

  /** True once a newer deployed build has been detected. */
  private readonly updateAvailableSignal = signal(false);

  /** True once a newer deployed build has been detected. */
  public readonly updateAvailable = this.updateAvailableSignal.asReadonly();

  /**
   * Captures the current build signature, then polls index.html on an interval and
   * emits on {@link updateAvailable$} when the asset set changes. Idempotent — later
   * calls are ignored.
   * @param pollIntervalMs poll cadence in milliseconds (default 5 minutes)
   */
  public start(pollIntervalMs = POLL_INTERVAL_MS): void {
    if (this.started) {
      return;
    }
    this.started = true;

    // Fire immediately (captures the baseline), then poll on the interval and
    // compare each result against that baseline. Stops polling once an update is
    // detected (the outcome can no longer change).
    timer(0, pollIntervalMs)
      .pipe(
        switchMap(() => this.fetchSignature()),
        filter((signature): signature is string => signature !== null),
        takeWhile(() => !this.updateAvailableSignal(), true),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(signature => this.evaluate(signature));
  }

  /**
   * Fetches index.html (auth-bypassed, cache-busted) and reduces it to an asset
   * signature. Emits null on any error so the poll loop keeps running.
   * @returns the build signature, or null when the fetch/parse fails
   */
  private fetchSignature(): Observable<string | null> {
    const url = `${INDEX_URL}?cb=${Date.now()}`;
    const context = new HttpContext().set(BYPASS_TOKEN_INJECTION, true);
    return this.http.get(url, { responseType: 'text', context }).pipe(
      map(html => this.extractSignature(html)),
      catchError(() => of(null)),
    );
  }

  /**
   * Builds a stable signature from the hashed asset URLs in an index.html document.
   * @param html the raw index.html markup
   * @returns the sorted, joined list of hashed asset URLs
   */
  private extractSignature(html: string): string {
    return [...html.matchAll(ASSET_PATTERN)].map(match => match[1]).sort().join('|');
  }

  /**
   * Compares a freshly polled signature against the baseline and flags an update
   * when they differ. Seeds the baseline if it was not captured at startup.
   * @param signature the signature from the latest poll
   */
  private evaluate(signature: string): void {
    if (!this.baselineSignature) {
      this.baselineSignature = signature;
      return;
    }
    if (signature !== this.baselineSignature) {
      this.updateAvailableSignal.set(true);
    }
  }
}
